import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  importProvidersFrom,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TitleStrategy, provideRouter } from '@angular/router';
import { provideSdc } from '@3ddv/software-division-components';
import { provideNgVibeDrawer } from '@ng-vibe/drawer';
import { firstValueFrom } from 'rxjs';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { exposeAuthDebugHandle } from './core/services/auth-debug.utility';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { globalLoaderInterceptor } from './core/interceptors/global-loader.interceptor';
import { AuthService } from './core/services/auth.service';
import { PageNavigationService } from './core/services/page-navigation.service';
import { SessionCookieService } from './core/services/session-cookie.service';
import { ClientInterceptor } from './private/interceptors/client.interceptor';
import { PrivateModule } from './private/private.module';
import { ConfigurationService } from './private/services/configuration.service';
import { TemplatePageTitleStrategyService } from './private/services/template-page-title-strategy.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      inject(ConfigurationService).init();

      const authService = inject(AuthService);
      if (!inject(SessionCookieService).read()) {
        return undefined;
      }

      return firstValueFrom(authService.loadSession()).catch(
        (error: unknown) => {
          if (environment.debug) {
            console.error('[AUTH-FLOW] loadSession failed on boot:', error);
          }
        }
      );
    }),
    provideAppInitializer(() => {
      inject(PageNavigationService);
    }),
    provideAppInitializer(() =>
      exposeAuthDebugHandle(inject(AuthService), environment.debug)
    ),
    provideHttpClient(
      withInterceptors([authInterceptor, globalLoaderInterceptor]),
      withInterceptorsFromDi()
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ClientInterceptor,
      multi: true,
    },
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategyService,
    },
    provideNgVibeDrawer(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideSdc({ theme: 'backoffice' }),
    provideRouter(routes),
    importProvidersFrom(
      BrowserAnimationsModule,
      ToastrModule.forRoot({
        timeOut: 10000,
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
      }),
      PrivateModule
    ),
  ],
};
